import { createContext, useContext, useState, useId, useRef, useEffect } from 'react';

import type { PropsWithChildren } from 'react';

export type SelectValue = string | number | null;

export type OptionValue = {
  value: SelectValue;
  label: string;
  selectedLabel?: string;
  disabled?: boolean;
};
export interface SelectListboxContext {
  options: OptionValue[];
  selectedValue: OptionValue;
  setSelectedValue: (v: OptionValue) => void;
  isOpen: boolean;
  setIsOpen: (v: boolean) => void;
  toggleListbox: () => void;
  getValue: (value: SelectValue) => OptionValue;
  activeIndex: number;
  setActiveIndex: (v: number) => void;
  listboxId: string;
  isDirty: boolean;
}

const SelectContext = createContext<SelectListboxContext>({} as SelectListboxContext);

interface Props {
  options: OptionValue[];
  defaultValue: SelectValue;
}

export const SelectProvider = ({ options, defaultValue, children }: PropsWithChildren<Props>) => {
  const [value, setValue] = useState<OptionValue>({ value: '', label: '' });
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isDirty, setIsDirty] = useState(false);
  const listboxId = useRef<string>(useId());

  const toggleListbox = () => {
    setIsOpen((status) => !status);
    setIsDirty(false);
  };

  const getValue = (value: SelectValue) => {
    return (
      options.find((option) => option.value === value) || {
        value: -1,
        label: '',
        selectedLabel: '-',
      }
    );
  };

  const setSelectedValue = (value: OptionValue, isDefault = false) => {
    const selectedIndex = options.findIndex((option) => option.value === value.value);
    setActiveIndex(selectedIndex);
    setValue(value);
    setIsOpen(false);
    setIsDirty(!isDefault);
  };

  if (defaultValue !== -1 && !value.value) {
    setSelectedValue(getValue(defaultValue), true);
  }

  useEffect(() => {
    const newLabel = getValue(value.value || defaultValue).label;
    if (newLabel !== value.label) {
      setSelectedValue(getValue(value.value || defaultValue));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, defaultValue]);

  return (
    <SelectContext.Provider
      value={{
        options,
        selectedValue: value,
        setSelectedValue,
        isOpen,
        setIsOpen,
        toggleListbox,
        getValue,
        activeIndex,
        setActiveIndex,
        listboxId: listboxId.current,
        isDirty,
      }}
    >
      {children}
    </SelectContext.Provider>
  );
};

export const useSelect = () => {
  const context = useContext(SelectContext);

  if (context === undefined) throw new Error('useSelect can only be used in an SelectProvider');

  return context;
};
